import React, { useCallback, useState } from "react";
import { Alert, Button, Input } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { FormatterUtils } from "@nubeteck/utils";
import toast from "react-hot-toast";
import { toastErrorStyle } from "src/constants";
import { useIniciarBacSimplePayMutation } from "src/services/cartServices";
import BacOtpStep from "./bac-otp-step";
import BacManualTransferStep from "./bac-manual-transfer-step";
import {
  StyledModal,
  ModalBody,
  CloseButton,
  PanelHeader,
  AmountDisplay,
  FormFields,
  FieldLabel,
  FieldError,
  AlertsContainer,
  ActionButtons,
} from "./bac-simple-pay-form.styled";

const PREREGISTER_TTL_MS = 10 * 60 * 1000;

type Step = "confirm" | "otp" | "manual";

interface BacSimplePayFormProps {
  open: boolean;
  content?: Record<string, string | undefined> | null;
  totalAmount: number;
  currencySymbol: string;
  people: string;
  cedula?: string;
  defaultPhone?: string;
  onSuccess: (pagoId: number) => void;
  onCancel: () => void;
}

function BacSimplePayForm({
  open,
  content,
  totalAmount,
  currencySymbol,
  people,
  cedula,
  defaultPhone,
  onSuccess,
  onCancel,
}: BacSimplePayFormProps) {
  const [step, setStep] = useState<Step>("confirm");
  const [identificacion, setIdentificacion] = useState(cedula ?? "");
  const [identificacionError, setIdentificacionError] = useState<string | undefined>(undefined);
  const [telefono, setTelefono] = useState(defaultPhone ?? "");
  const [telefonoError, setTelefonoError] = useState<string | undefined>(undefined);
  const [preregisterId, setPreregisterId] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number>(0);
  const [resendCount, setResendCount] = useState(0);
  const [orgPhone, setOrgPhone] = useState<string | undefined>(undefined);

  const [iniciarPago, { isLoading: isIniciando }] = useIniciarBacSimplePayMutation();

  const resetAll = useCallback(() => {
    setStep("confirm");
    setIdentificacion(cedula ?? "");
    setIdentificacionError(undefined);
    setTelefono(defaultPhone ?? "");
    setTelefonoError(undefined);
    setPreregisterId(null);
    setExpiresAt(0);
    setResendCount(0);
    setOrgPhone(undefined);
  }, [cedula, defaultPhone]);

  const handleCancel = () => {
    resetAll();
    onCancel();
  };

  const handleConfirm = async () => {
    let hasError = false;

    if (!identificacion.trim()) {
      setIdentificacionError(content?.bac_id_required || "La identificación es requerida");
      hasError = true;
    }

    if (telefono && !/^\d{8}$/.test(telefono)) {
      setTelefonoError(content?.bac_phone_invalid || "El teléfono debe tener 8 dígitos");
      hasError = true;
    }

    if (hasError) return;

    const result: any = await iniciarPago({
      people,
      monto: totalAmount.toFixed(2),
      clientIdentification: identificacion.trim(),
      clientPhone: telefono || undefined,
    });

    if (result?.data?.responseStatus === "Success" && result?.data?.data?.preregisterId) {
      const data = result.data.data;
      setPreregisterId(data.preregisterId);
      setExpiresAt(Date.now() + PREREGISTER_TTL_MS);
      setResendCount(0);
      setOrgPhone(data.orgPhone);
      setStep(data.requiresOtp ? "otp" : "manual");
    } else {
      const message =
        result?.error?.data?.message ||
        result?.data?.message ||
        content?.bac_init_error ||
        "No fue posible iniciar el pago con SINPE Móvil";
      toast.error(message, toastErrorStyle);
    }
  };

  const handleExpire = () => {
    toast.error(
      content?.bac_payment_expired_message ||
        "El tiempo para completar el pago venció, inicie el proceso nuevamente",
      toastErrorStyle,
    );
    resetAll();
  };

  const formattedAmount = FormatterUtils.number(totalAmount.toFixed(2));

  return (
    <StyledModal
      open={open}
      onCancel={handleCancel}
      maskClosable={false}
      closable={false}
      title={null}
      footer={null}
      width={520}
      centered
    >
      <ModalBody>
        {!isIniciando && (
          <CloseButton onClick={handleCancel} type="button">
            <CloseOutlined />
          </CloseButton>
        )}

        <PanelHeader>
          <h3>{content?.bac_form_title || "Pago con SINPE Móvil (BAC)"}</h3>
          <p>{content?.bac_form_subtitle || "Confirme sus datos para iniciar el pago"}</p>
        </PanelHeader>

        <AmountDisplay>
          <span className="label">{content?.total_to_pay_label || "Total a pagar"}</span>
          <span className="amount">
            {currencySymbol}
            {formattedAmount}
          </span>
        </AmountDisplay>

        {step === "confirm" && (
          <>
            <FormFields>
              <div>
                <FieldLabel>{content?.bac_id_label || "Identificación"}</FieldLabel>
                <Input
                  size="large"
                  placeholder={
                    content?.bac_id_placeholder || "Cédula, DIMEX, DIDI, pasaporte, etc."
                  }
                  value={identificacion}
                  onChange={(e) => {
                    setIdentificacion(e.target.value);
                    if (identificacionError) setIdentificacionError(undefined);
                  }}
                  status={identificacionError ? "error" : undefined}
                />
                {identificacionError && <FieldError>{identificacionError}</FieldError>}
              </div>
              <div>
                <FieldLabel>
                  {content?.bac_phone_label || "Teléfono SINPE Móvil (opcional)"}
                </FieldLabel>
                <Input
                  size="large"
                  addonBefore="+506"
                  placeholder={content?.bac_phone_placeholder || "88882355"}
                  value={telefono}
                  maxLength={8}
                  onChange={(e) => {
                    setTelefono(e.target.value.replace(/\D/g, "").slice(0, 8));
                    if (telefonoError) setTelefonoError(undefined);
                  }}
                  status={telefonoError ? "error" : undefined}
                />
                {telefonoError && <FieldError>{telefonoError}</FieldError>}
              </div>
            </FormFields>

            <AlertsContainer>
              <Alert
                type="info"
                showIcon
                message={
                  content?.bac_info_message ||
                  "Si es cliente BAC recibirá un código de verificación por SMS. Si no, podrá pagar mediante una transferencia SINPE Móvil."
                }
              />
            </AlertsContainer>

            <ActionButtons>
              <Button
                className="cancel-btn"
                size="large"
                onClick={handleCancel}
                disabled={isIniciando}
              >
                {content?.cancel_button || "Cancelar"}
              </Button>
              <Button
                className="pay-btn"
                type="primary"
                size="large"
                loading={isIniciando}
                onClick={handleConfirm}
              >
                {content?.bac_continue_button || "Continuar"}
              </Button>
            </ActionButtons>
          </>
        )}

        {step === "otp" && preregisterId && (
          <BacOtpStep
            content={content}
            preregisterId={preregisterId}
            expiresAt={expiresAt}
            resendCount={resendCount}
            onResendCountChange={setResendCount}
            onExpire={handleExpire}
            onCancel={handleCancel}
            onSuccess={onSuccess}
            onRestart={resetAll}
          />
        )}

        {step === "manual" && preregisterId && (
          <BacManualTransferStep
            content={content}
            preregisterId={preregisterId}
            totalAmount={totalAmount}
            currencySymbol={currencySymbol}
            orgPhone={orgPhone}
            expiresAt={expiresAt}
            onExpire={handleExpire}
            onCancel={handleCancel}
            onSuccess={onSuccess}
            onRestart={resetAll}
          />
        )}
      </ModalBody>
    </StyledModal>
  );
}

export default BacSimplePayForm;
